/**
 * Registers the Alpine.js mobileHeaderMenu component.
 * Call before Alpine.start().
 *
 * @param {import('alpinejs').Alpine} Alpine
 * @param {string} shopUrl  The WooCommerce shop page URL (passed from PHP).
 */
export const registerMobileHeaderMenu = (Alpine) => {
  Alpine.data('mobileHeaderMenu', (shopUrl = '') => {
    /* ── pure helpers (no Alpine dependency) ─────────────────────── */

    const getDirectSubmenus = (item) =>
      Array.from(item.children).filter((child) => {
        if (child.tagName !== 'UL') return false
        return Array.from(child.children).some((li) =>
          Array.from(li.children).some((c) => c.tagName === 'A'),
        )
      })

    const normalizeUrl = (url) => {
      try {
        return new URL(url, window.location.origin).pathname.replace(/\/$/, '')
      } catch {
        return String(url).replace(/\/$/, '')
      }
    }

    const removeInlineLinkIcons = (link) => link.querySelectorAll('svg').forEach((svg) => svg.remove())

    /* ── Alpine component definition ─────────────────────────────── */

    return {
      shopUrl,

      /* reactive state */
      open: false,
      submenuPanelOpen: false,
      submenuTitle: '',
      submenuHtml: '',

      /* private (non-reactive) */
      _submenuStack: [],
      _expandChildren: false,

      focusableSelector: 'a[href], button:not([disabled]), input:not([disabled])',

      /* ── lifecycle ──────────────────────────────────────────────── */

      init() {
        /* re-enhance submenu list after x-html re-renders it */
        this.$watch('submenuHtml', () => {
          this.$nextTick(() => this._setupSubmenuList())
        })

        /* lock body scroll when drawer opens — use a ref-count so other drawers
           on the same page don't clobber each other's lock */
        this.$watch('open', (val) => {
          window.__scrollLockCount = (window.__scrollLockCount || 0) + (val ? 1 : -1)
          if (window.__scrollLockCount <= 0) {
            window.__scrollLockCount = 0
            document.body.style.overflow = ''
          } else {
            document.body.style.overflow = 'hidden'
          }
        })

        /* enhance root menu items for drilldown on first render */
        this.$nextTick(() => {
          if (this.$refs.menuContent) {
            this._enhanceMenuList(this.$refs.menuContent, {
              allowRootItems: true,
              allowShopTemplate: true,
            })
          }
        })

        /* close on desktop resize */
        this._resizeHandler = () => {
          if (window.matchMedia('(min-width: 1400px)').matches && this.open) {
            this.close()
          }
        }
        window.addEventListener('resize', this._resizeHandler)
      },

      destroy() {
        window.removeEventListener('resize', this._resizeHandler)
        /* release scroll lock if drawer was open when component unmounts */
        if (this.open) {
          window.__scrollLockCount = Math.max(0, (window.__scrollLockCount || 1) - 1)
          if (window.__scrollLockCount === 0) document.body.style.overflow = ''
        }
      },

      /* ── public API (used in x-on directives) ───────────────────── */

      toggle() {
        this.open = !this.open
        if (!this.open) this._closeSubmenuPanel()
        else this.$nextTick(() => this.$el.querySelector(this.focusableSelector)?.focus())
      },

      close() {
        this.open = false
        this._closeSubmenuPanel()
      },

      goBack() {
        const previous = this._submenuStack.pop()

        if (!previous) {
          this._closeSubmenuPanel()
          this.$nextTick(() => this.$refs.menuContent?.querySelector(this.focusableSelector)?.focus())
          return
        }

        /* slide out, swap content, slide back in */
        this.submenuPanelOpen = false
        this.$nextTick(() => {
          this.submenuTitle = previous.title
          this._expandChildren = this._submenuStack.length > 0
          this.submenuHtml = previous.html
          this.submenuPanelOpen = true
          this.$nextTick(() => this.$refs.submenuPanel?.querySelector(this.focusableSelector)?.focus())
        })
      },

      /* ── private helpers ────────────────────────────────────────── */

      _closeSubmenuPanel() {
        this.submenuPanelOpen = false
        this._submenuStack = []
        this._expandChildren = false
        this.submenuTitle = ''
        this.submenuHtml = ''
      },

      _isShopLink(link) {
        // Primary: PHP marks the shop <li> with js-mobile-shop-item — always trust this
        const li = link.closest('li')
        if (li && li.classList.contains('js-mobile-shop-item')) return true

        // Fallback: exact URL match only (no label matching — too broad across environments)
        return normalizeUrl(link.href) === normalizeUrl(this.shopUrl) && this.shopUrl !== ''
      },

      _getShopCategorySubmenus() {
        const template = this.$refs.shopCategoriesTemplate
        if (!template) return []
        const wrapper = document.createElement('div')
        wrapper.innerHTML = template.innerHTML.trim()
        return Array.from(wrapper.children)
      },

      _openSubmenu(title, href, submenus, options = {}) {
        const { expandChildren = false } = options

        if (this.submenuPanelOpen) {
          this._submenuStack.push({ title: this.submenuTitle, html: this.submenuHtml })
        }

        this._expandChildren = expandChildren || this._submenuStack.length > 0
        this.submenuPanelOpen = false

        this.$nextTick(() => {
          this._renderSubmenu(title, submenus)
          this.submenuPanelOpen = true
          this.$nextTick(() => this.$refs.submenuPanel?.querySelector(this.focusableSelector)?.focus())
        })
      },

      _renderSubmenu(title, submenus) {
        this.submenuTitle = title

        if (!submenus.length) {
          this.submenuHtml = '<p class="text-sm text-extra-light-grey">Inga länkar hittades.</p>'
          return
        }

        const wrapper = document.createElement('div')
        submenus.forEach((submenu) => {
          const cloned = submenu.cloneNode(true)
          cloned.classList.remove('hidden')
          cloned.classList.add('space-y-1', 'list-none', 'pl-0')
          wrapper.appendChild(cloned)
        })
        this.submenuHtml = wrapper.innerHTML
      },

      _setupSubmenuList() {
        const list = this.$refs.submenuList
        if (!list || !this.submenuHtml) return

        if (this._expandChildren || this._submenuStack.length > 0) {
          this._expandAllSubmenus(list)
        } else {
          this._enhanceMenuList(list, { allowDirectSubmenus: true })
        }
      },

      _enhanceMenuList(container, options = {}) {
        const { allowShopTemplate = false, allowRootItems = false, allowDirectSubmenus = false } = options
        const items = Array.from(container.querySelectorAll('li')).filter((item) => !item.closest('template'))

        items.forEach((item) => {
          let link = Array.from(item.children).find((child) => child.tagName === 'A')
          if (!link) return

          /* clean up any previously injected buttons */
          Array.from(item.children)
            .filter((child) => child.tagName === 'BUTTON' && child.dataset.mobileDrilldownArrow === 'true')
            .forEach((btn) => btn.remove())
          delete item.dataset.mobileDrilldownReady
          link.classList.remove('pr-12')

          const shopSubmenus = allowShopTemplate && this._isShopLink(link) ? this._getShopCategorySubmenus() : []
          const directSubmenus = getDirectSubmenus(item)
          const isRootMenuItem = allowRootItems && item.parentElement?.parentElement === container
          const isDrilldownSource =
            item.dataset.mobileDrilldownSource === 'true' ||
            isRootMenuItem ||
            allowDirectSubmenus ||
            shopSubmenus.length > 0
          const allowedDirectSubmenus = isDrilldownSource ? directSubmenus : []
          const submenus = shopSubmenus.length ? shopSubmenus : allowedDirectSubmenus

          if (!submenus.length || item.dataset.mobileDrilldownReady === 'true') return

          item.dataset.mobileDrilldownReady = 'true'
          item.classList.add('relative')
          allowedDirectSubmenus.forEach((s) => s.classList.add('hidden'))

          const cleanLink = link.cloneNode(true)
          removeInlineLinkIcons(cleanLink)
          link.replaceWith(cleanLink)
          link = cleanLink
          link.classList.add('pr-12')

          const arrowButton = document.createElement('button')
          arrowButton.type = 'button'
          arrowButton.dataset.mobileDrilldownArrow = 'true'
          arrowButton.className =
            'absolute right-0 top-1 grid size-10 place-items-center rounded-md text-white transition hover:bg-green'
          arrowButton.setAttribute('aria-label', link.textContent.trim())
          arrowButton.innerHTML =
            '<svg class="size-4" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>'

          const openFromItem = (event) => {
            event.preventDefault()
            this._openSubmenu(link.textContent.trim(), link.href, submenus, {
              expandChildren: !shopSubmenus.length && isRootMenuItem,
            })
          }

          link.addEventListener('click', openFromItem)
          arrowButton.addEventListener('click', openFromItem)
          item.insertBefore(arrowButton, allowedDirectSubmenus[0] || null)
        })
      },

      _expandAllSubmenus(container) {
        const rootLists = Array.from(container.children).filter((child) => child.tagName === 'UL')
        const items = Array.from(container.querySelectorAll('li')).filter((item) => !item.closest('template'))

        items.forEach((item) => {
          let link = Array.from(item.children).find((child) => child.tagName === 'A')
          const directSubmenus = getDirectSubmenus(item)
          const isTopLevelItem = rootLists.some((list) => item.parentElement === list)

          Array.from(item.children)
            .filter(
              (child) =>
                child.tagName === 'BUTTON' &&
                (child.dataset.mobileInlineToggle === 'true' || child.dataset.mobileDrilldownArrow === 'true'),
            )
            .forEach((btn) => btn.remove())

          if (link) {
            const cleanLink = link.cloneNode(true)
            removeInlineLinkIcons(cleanLink)
            cleanLink.classList.remove('pr-12')
            link.replaceWith(cleanLink)
            link = cleanLink
          }

          if (!directSubmenus.length) return

          if (isTopLevelItem) {
            directSubmenus.forEach((s) => s.classList.remove('hidden'))
            return
          }

          if (!link) return

          item.classList.add('relative')
          directSubmenus.forEach((s) => s.classList.add('hidden'))
          link.classList.add('pr-12')

          const toggleButton = document.createElement('button')
          toggleButton.type = 'button'
          toggleButton.dataset.mobileInlineToggle = 'true'
          toggleButton.className =
            'absolute right-0 top-0 grid size-9 place-items-center rounded-md text-white transition hover:bg-green'
          toggleButton.setAttribute('aria-expanded', 'false')
          toggleButton.setAttribute('aria-label', link.textContent.trim())
          toggleButton.innerHTML =
            '<svg class="size-4 transition-transform" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>'

          toggleButton.addEventListener('click', () => {
            const isOpen = toggleButton.getAttribute('aria-expanded') === 'true'
            toggleButton.setAttribute('aria-expanded', String(!isOpen))
            toggleButton.querySelector('svg')?.classList.toggle('rotate-180', !isOpen)
            directSubmenus.forEach((s) => s.classList.toggle('hidden', isOpen))
          })

          item.insertBefore(toggleButton, directSubmenus[0])
        })
      },
    }
  })
}
